import classNames from 'classnames';
import React, {useEffect, useMemo} from 'react';

import {
    AsyncPriceCourseCard,
    AsyncPriceCourseCardContext,
    BrowseCourse,
} from '@udemy/browse-course';
import {DiscoveryItemImpressionEvent, discoveryTracker} from '@udemy/browse-event-tracking';
import {UI_REGION} from '@udemy/browse-utils';
import {DiscoveryUnit, DiscoveryUnitItem} from '@udemy/discovery-api';
import {TrackImpression, Tracker, TrackingContextProvider} from '@udemy/event-tracking';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n, useFormatNumber} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {VoucherCard} from '@udemy/react-certification-preparation';
import {Button} from '@udemy/react-core-components';
import {ExperimentImpressionEvent} from '@udemy/react-experimentation-provider';
import {ContentArea} from '@udemy/react-layout-components';
import {TextSkeleton} from '@udemy/react-reveal-components';
import {
    FALLBACK_COLLECTION_STATS,
    PlanPeriod,
    PlanPeriodStyle,
    SubscriptionLearnMoreEvent,
    SubscriptionOfferImpressionEvent,
    usePersonalPlanCollectionStats,
} from '@udemy/subscription-browse';
import {
    SubscriptionPlanProductType,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {PersonalPlanSubscriptionCTA} from 'src/features/personal-plan-landing-page/components/personal-plan-subscription-cta/personal-plan-subscription-cta.react-component';

import styles from './topic-pp-merchandising-module.module.less';
import {calculateBenefits} from './utils';

const SUBSCRIPTION_CHECKOUT_ENTRY_TOPIC_PP_MERCH = 'topic_pp_merchandising';

/* Module-level uiRegion so this impression is differentiable in analytics from
   `SubscriptionCTA`'s own auto-fired impression (which uses `UI_REGION.PERSONAL_PLAN`). */
const TOPIC_PP_MERCH_MODULE_UI_REGION = 'topic_pp_merchandising_module';

function isCourseDiscoveryItem(item: DiscoveryUnitItem): boolean {
    return (item as {_class?: string})._class === 'course';
}

export type PpMerchandisingVariant = 'general' | 'cert';

export interface BaseTopicPpMerchandisingModuleProps {
    /** Discovery unit supplying the carousel courses. Routed in via the topic discovery-units container. */
    unit?: DiscoveryUnit;
    topicName: string;
    isMobile?: boolean;
    topicSubscriptionUpsellFeature?: FeatureVariantAssignment;
}

export interface GeneralTopicPpMerchandisingModuleProps
    extends BaseTopicPpMerchandisingModuleProps {
    variant: 'general' | undefined;
}

// TODO: Update this interface with the real shape of the data once https://udemy.atlassian.net/browse/CER-364
// is finished.
export interface CertTopicPpMerchandisingModuleProps extends BaseTopicPpMerchandisingModuleProps {
    numPrepCourses: number;
    numPracticeExams: number;
    examTitle: string;
    examProvider: string;
    examAggregatorUrl: string;
    offersRetakeInsurance?: boolean;
    variant: 'cert';
}

export type TopicPpMerchandisingModuleProps =
    | GeneralTopicPpMerchandisingModuleProps
    | CertTopicPpMerchandisingModuleProps;

export const TopicPpMerchandisingModule: React.FC<TopicPpMerchandisingModuleProps> = (props) => {
    const {unit, topicName, isMobile, topicSubscriptionUpsellFeature} = props;
    const i18n = useI18n();
    const {formatNumber} = useFormatNumber();
    const {gettext, interpolate} = i18n;
    const {request} = useUDData();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const isDesktop = useMatchMedia('lg-min');

    const collectionStatsData = usePersonalPlanCollectionStats();
    const isCollectionStatsLoading = collectionStatsData?.isLoading ?? false;
    const contentCollectionStats =
        collectionStatsData && 'numberOfCourses' in collectionStatsData
            ? collectionStatsData
            : undefined;
    const courseCount =
        contentCollectionStats?.numberOfCourses ?? FALLBACK_COLLECTION_STATS.numberOfCourses;

    useEffect(() => {
        const assignment = topicSubscriptionUpsellFeature;
        if (!assignment) {
            return;
        }
        const {experimentIds = [], isLocalDefault = false} = assignment;
        Tracker.publishEvent(new ExperimentImpressionEvent(experimentIds, isLocalDefault));
    }, [topicSubscriptionUpsellFeature]);

    const courses = useMemo(() => {
        return (unit?.items ?? []).filter(isCourseDiscoveryItem) as BrowseCourse[];
    }, [unit?.items]);

    if (courses.length === 0) {
        return null;
    }

    const isCert = ((
        props: TopicPpMerchandisingModuleProps,
    ): props is CertTopicPpMerchandisingModuleProps => {
        return props.variant === 'cert';
    })(props);

    let headline;
    // The examTitle should never be used outside of the isCert instance, but we define it here
    // to satisfy TSC. Otherwise, TSC gets confused about whether the type exists or not.
    const examTitle = isCert ? props.examTitle : undefined;

    if (isCert) {
        headline = interpolate(
            gettext(
                'Prepare smarter for the %(examTitle)s exam with Premium content on Personal Plan',
            ),
            {examTitle: props.examTitle},
            true,
        );
    } else {
        headline = interpolate(
            gettext('Get access to Premium courses for %(topic)s with Personal Plan'),
            {topic: topicName},
            true,
        );
    }

    let subhead;
    if (isCert) {
        subhead = gettext(
            'Access high-quality courses, realistic practice exams, and built-in support to strengthen your readiness for test day.',
        );
    } else {
        subhead = gettext(
            'With thousands of our best-rated courses from top Udemy instructors, Personal Plan is your subscription to success. Explore some of the included content below.',
        );
    }

    // TODO: revisit once the cert variant designs are finalized. Suppressing
    // render for now while design iterates.
    if (isCert) {
        return null;
    }

    const trackLearnMoreClick = () => {
        if (!personalPlanContext || !('defaultPlanOption' in personalPlanContext)) {
            return;
        }
        const learnMoreFields = {
            planPriceId: personalPlanContext.defaultPlanOption.id,
            planType: SubscriptionPlanProductType.Consumersubscription,
            uiRegion: TOPIC_PP_MERCH_MODULE_UI_REGION,
            planPriceIds: personalPlanContext.planPriceOptionIds ?? null,
            noticeId: null,
        };
        Tracker.publishEvent(
            new SubscriptionLearnMoreEvent(
                learnMoreFields.planPriceId,
                learnMoreFields.planType,
                learnMoreFields.uiRegion,
                learnMoreFields.planPriceIds,
                learnMoreFields.noticeId,
            ),
        );
    };

    const trackSubscriptionOfferImpression = () => {
        // Narrow the discriminated union returned by `useConsumerSubscriptionPlan` to the loaded
        // ConsumerSubscriptionPlan variant — the loading and subscriber states don't carry pricing.
        if (!personalPlanContext || !('defaultPlanOption' in personalPlanContext)) {
            return;
        }
        // Named-keys form for readability before the constructor's positional argument list —
        // `SubscriptionOfferImpressionEvent` doesn't accept an options object directly.
        const offerFields = {
            planPriceId: personalPlanContext.defaultPlanOption.id,
            courseId: null,
            uiRegion: TOPIC_PP_MERCH_MODULE_UI_REGION,
            courseTrackingId: undefined,
            hasTrial: !!personalPlanContext.trialPeriodDays,
            trialDays: personalPlanContext.trialPeriodDays ?? null,
            badges: null,
            planPriceIds: personalPlanContext.planPriceOptionIds ?? null,
            noticeId: null,
        };
        Tracker.publishEvent(
            new SubscriptionOfferImpressionEvent(
                offerFields.planPriceId,
                offerFields.courseId,
                offerFields.uiRegion,
                offerFields.courseTrackingId,
                offerFields.hasTrial,
                offerFields.trialDays,
                offerFields.badges,
                offerFields.planPriceIds,
                offerFields.noticeId,
            ),
        );
    };

    const benefits = calculateBenefits(courseCount, isCollectionStatsLoading, i18n, formatNumber);

    return (
        <TrackImpression trackFunc={trackSubscriptionOfferImpression} visibilityThreshold={0.01}>
            <section
                className={styles.root}
                data-purpose="topic-pp-merchandising-module"
                aria-labelledby="topic-pp-merchandising-heading"
            >
                <div className={styles.inner}>
                    <header className={styles.header}>
                        <h2 id="topic-pp-merchandising-heading" className={styles.title}>
                            {headline}
                        </h2>
                        <p className={styles.subtitle}>{subhead}</p>
                    </header>
                    <div className={styles.grid}>
                        <div className={styles.courses}>
                            <AsyncPriceCourseCardContext.Provider
                                value={{useCourseProductCard: true, hideCourseDescription: true}}
                            >
                                {/* `BrowseCourseProductCard` thumbnails rely on a `ud-content-area` container query;
                                without this wrapper the condensed/horizontal media slots collapse to zero width. */}
                                <ContentArea className={styles['courses-list']}>
                                    {courses.map((course, index) => (
                                        <TrackingContextProvider
                                            key={course.id}
                                            trackingContext={{
                                                trackImpressionFunc:
                                                    discoveryTracker.trackDiscoveryImpression,
                                                index,
                                                backendSource:
                                                    DiscoveryItemImpressionEvent
                                                        .backendSourceOptions.DISCOVERY,
                                            }}
                                        >
                                            <div
                                                className={styles['course-card-slot']}
                                                data-purpose="topic-pp-merchandising-course"
                                            >
                                                <AsyncPriceCourseCard
                                                    className={styles['pp-upsell-course-card']}
                                                    course={course as BrowseCourse}
                                                    layout={isDesktop ? 'horizontal' : 'condensed'}
                                                    showDetails={false}
                                                    showCourseTag={false}
                                                    showPrice={false}
                                                />
                                            </div>
                                        </TrackingContextProvider>
                                    ))}
                                    {/* TODO: revisit when the cert variant suppression is removed. */}
                                    {isCert ? (
                                        <>
                                            {isCollectionStatsLoading ? (
                                                <TextSkeleton
                                                    lineCountPerParagraph={1}
                                                    paragraphCount={1}
                                                />
                                            ) : (
                                                <span
                                                    className={classNames(
                                                        'ud-heading-xl',
                                                        styles['footer-title'],
                                                    )}
                                                >
                                                    {interpolate(
                                                        gettext(
                                                            'Access these courses and over %(courses)s more, plus:',
                                                        ),
                                                        {courses: formatNumber(courseCount)},
                                                        true,
                                                    )}
                                                </span>
                                            )}
                                            <div className={styles['voucher-banner']}>
                                                <VoucherCard
                                                    className={styles['voucher-card']}
                                                    logoSrc="https://frontends-dev.udemycdn.com/staticx/udemy/images/certification-vouchers/comptia_security_plus_cert.svg"
                                                />
                                                <span className={'ud-heading-lg'}>
                                                    {interpolate(
                                                        gettext(
                                                            'Exclusive deals on  %(examTitle)s exam vouchers',
                                                        ),
                                                        {examTitle: examTitle},
                                                        true,
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    ) : null}
                                </ContentArea>
                            </AsyncPriceCourseCardContext.Provider>
                        </div>
                        <div className={styles['plan-card']}>
                            <div className={styles['plan-card-body']}>
                                <div className={styles['plan-card-header']}>
                                    <div
                                        className={classNames(
                                            'ud-heading-xxl',
                                            styles['plan-name'],
                                        )}
                                    >
                                        <span className={styles['plan-name-personal']}>
                                            {gettext('Personal')}
                                        </span>{' '}
                                        <span className={styles['plan-name-plan']}>
                                            {gettext('Plan')}
                                        </span>
                                    </div>
                                    <div
                                        className={classNames('ud-text-md', styles['plan-tagline'])}
                                    >
                                        {gettext('Unlock your career potential.')}
                                    </div>
                                </div>
                                <ul className={styles.benefits}>
                                    {benefits.map(({icon, text}, index) => (
                                        <li
                                            key={index}
                                            className={classNames(
                                                'ud-text-sm',
                                                styles['benefit-row'],
                                            )}
                                        >
                                            <Icon
                                                glyph={icon}
                                                label={false}
                                                size="small"
                                                className={styles['benefit-icon']}
                                            />
                                            <span>{text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className={styles['plan-card-footer']}>
                                {personalPlanContext &&
                                    !personalPlanContext.isLoading &&
                                    'id' in personalPlanContext && (
                                        <>
                                            <div className={styles['plan-period']}>
                                                <PlanPeriod
                                                    listPriceText={
                                                        personalPlanContext.formattedMonthlyAmount
                                                    }
                                                    trialPeriodDays={
                                                        personalPlanContext.trialPeriodDays
                                                    }
                                                    showStartingAtPrice={
                                                        !!personalPlanContext.annualPlanOption
                                                    }
                                                    style={PlanPeriodStyle.SINGLE}
                                                />
                                            </div>
                                            <PersonalPlanSubscriptionCTA
                                                className={styles['primary-cta']}
                                                plan={personalPlanContext}
                                                uiRegion={UI_REGION.PERSONAL_PLAN}
                                                subscriptionCheckoutEntry={
                                                    SUBSCRIPTION_CHECKOUT_ENTRY_TOPIC_PP_MERCH
                                                }
                                                planPeriodStyle={
                                                    request.isMobile || isMobile
                                                        ? PlanPeriodStyle.MULTILINE_NO_MARGIN
                                                        : PlanPeriodStyle.SINGLE
                                                }
                                                showSubtitle={false}
                                                purchaseCtaText={`${gettext('Start subscription')} →`}
                                            />
                                        </>
                                    )}
                                <Button
                                    componentClass="a"
                                    href="/personal-plan/"
                                    udStyle="secondary"
                                    size="large"
                                    className={styles['secondary-cta']}
                                    onClick={trackLearnMoreClick}
                                >
                                    {gettext('Learn more')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </TrackImpression>
    );
};
